"use client";

import React, { useRef, useState, useEffect, useCallback } from 'react';

interface Props {
    children: React.ReactNode;
    sectionId: string;
}

const TrendingProductsSlider = ({ children, sectionId }: Props) => {
    const trackRef = useRef<HTMLDivElement>(null);
    const [isHovered, setIsHovered] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const autoPlayRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const slides = React.Children.toArray(children);

    // Detect mobile (< 640px = Tailwind's sm breakpoint)
    useEffect(() => {
        const mq = window.matchMedia('(max-width: 639px)');
        setIsMobile(mq.matches);
        const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, []);

    // Mobile: show buttons when >1 card; Desktop: show buttons when >4 cards
    const showButtons = isMobile ? slides.length > 1 : slides.length > 4;

    // Read actual rendered card width + gap for precise per-card snapping
    const getScrollStep = useCallback(() => {
        const track = trackRef.current;
        if (!track) return 0;
        const firstCard = track.children[0] as HTMLElement | undefined;
        if (!firstCard) return track.clientWidth / 4;
        const gap = parseInt(getComputedStyle(track).gap) || 16;
        return firstCard.offsetWidth + gap;
    }, []);

    // Wraparound: next at end → jump to start, prev at start → jump to end
    const scrollByAmount = useCallback((direction: 'prev' | 'next') => {
        const track = trackRef.current;
        if (!track) return;
        const step = getScrollStep();
        if (direction === 'next') {
            const atEnd = track.scrollLeft >= track.scrollWidth - track.clientWidth - 4;
            if (atEnd) {
                track.scrollTo({ left: 0, behavior: 'smooth' });
            } else {
                track.scrollBy({ left: step, behavior: 'smooth' });
            }
        } else {
            const atStart = track.scrollLeft <= 4;
            if (atStart) {
                track.scrollTo({ left: track.scrollWidth, behavior: 'smooth' });
            } else {
                track.scrollBy({ left: -step, behavior: 'smooth' });
            }
        }
    }, [getScrollStep]);

    const prev = useCallback(() => scrollByAmount('prev'), [scrollByAmount]);
    const next = useCallback(() => scrollByAmount('next'), [scrollByAmount]);

    // Auto-play only when slider is needed
    useEffect(() => {
        if (!showButtons) return;
        if (isHovered) {
            if (autoPlayRef.current) clearInterval(autoPlayRef.current);
            return;
        }
        autoPlayRef.current = setInterval(() => {
            const track = trackRef.current;
            if (!track) return;
            const atEnd = track.scrollLeft >= track.scrollWidth - track.clientWidth - 4;
            if (atEnd) {
                track.scrollTo({ left: 0, behavior: 'smooth' });
            } else {
                next();
            }
        }, 4000);
        return () => { if (autoPlayRef.current) clearInterval(autoPlayRef.current); };
    }, [isHovered, next, showButtons]);

    return (
        <div
            className={`relative w-full ${showButtons ? 'mx-8' : ''}`}
            style={showButtons ? { width: 'calc(100% - 4rem)' } : {}}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {/* Previous Arrow */}
            {showButtons && (
                <button
                    onClick={prev}
                    aria-label="Previous"
                    className="absolute -left-10 top-1/2 -translate-y-1/2 z-20 w-9 h-9 flex items-center justify-center bg-white text-[#F97316] rounded-full border border-gray-200 shadow-md transition-all duration-300 hover:bg-[#F97316] hover:text-white hover:scale-110 focus:outline-none"
                >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                    </svg>
                </button>
            )}

            {/* Next Arrow */}
            {showButtons && (
                <button
                    onClick={next}
                    aria-label="Next"
                    className="absolute -right-10 top-1/2 -translate-y-1/2 z-20 w-9 h-9 flex items-center justify-center bg-white text-[#F97316] rounded-full border border-gray-200 shadow-md transition-all duration-300 hover:bg-[#F97316] hover:text-white hover:scale-110 focus:outline-none"
                >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                </button>
            )}

            {/* Track */}
            <div
                ref={trackRef}
                id={`slider-track-${sectionId}`}
                className={`flex gap-3 md:gap-4 overflow-x-auto pb-4 pt-2 ${!showButtons ? 'justify-center' : ''}`}
                style={{
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    scrollSnapType: showButtons ? 'x mandatory' : 'none',
                    WebkitOverflowScrolling: 'touch',
                }}
            >
                <style>{`#slider-track-${sectionId}::-webkit-scrollbar { display: none; }`}</style>
                {slides.map((child, i) => (
                    <div
                        key={i}
                        className="flex-shrink-0 transition-all duration-300 w-full sm:w-[80%] md:w-[47%] lg:w-[calc(25%-12px)] xl:w-[calc(25%-12px)]"
                        style={{ scrollSnapAlign: showButtons ? 'start' : 'none' }}
                    >
                        {child}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default TrendingProductsSlider;
